import { stripSpeakerOrgSuffix } from '../pages/files/utils/audio-segments';
import { canonicalizeSpeakerDisplayName } from './speaker-display-names';

export type RoleBadgeVariant = 'chair' | 'director' | 'ttk' | 'ttkAlt' | 'secretary' | 'reporter' | 'default';

export const ROLE_SORT_ORDER: Record<RoleBadgeVariant, number> = {
  chair: 0,
  director: 1,
  secretary: 2,
  reporter: 3,
  ttk: 4,
  ttkAlt: 5,
  default: 6,
};

export type ResolvedSpeakerRole = {
  personName: string;
  orgHint: string;
  label: string;
  variant: RoleBadgeVariant;
};

const BADGE_ROLE_PATTERNS = [
  /^руководитель$/i,
  /^сотрудник\s+ттк$/i,
  /^председательствующий$/i,
  /^chair$/i,
  /^секретарь$/i,
  /^докладчик$/i,
];

export function isJobTitleRole(role?: string): boolean {
  const text = (role || '').trim();
  if (!text || text === 'Участник') return false;
  if (isBadgeRoleLabel(text)) return false;
  return text.length > 12
    || /директор|коммерческ|транстелеком|филиал|начальник|руководител/i.test(text);
}

export function isBadgeRoleLabel(role?: string): boolean {
  const text = (role || '').trim();
  if (!text) return false;
  return BADGE_ROLE_PATTERNS.some(p => p.test(text));
}

/** «Галихан Мухамед-Рахимов (ТТК)» → { personName, orgHint } */
export function parseSpeakerLine(raw: string): { personName: string; orgHint: string } {
  const trimmed = (raw || '').trim();
  if (!trimmed) return { personName: '—', orgHint: '' };

  const paren = trimmed.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (paren) {
    return { personName: paren[1].trim(), orgHint: paren[2].trim() };
  }
  return { personName: canonicalizeSpeakerDisplayName(stripSpeakerOrgSuffix(trimmed)), orgHint: '' };
}

function normalizePersonKey(name: string): string {
  return canonicalizeSpeakerDisplayName(stripSpeakerOrgSuffix(name)).toLowerCase().replace(/\s+/g, ' ').trim();
}

function isGalikhanLeader(key: string): boolean {
  return key.startsWith('галихан') || key.startsWith('мухамед-рахимов');
}

function looksLikeMappedPerson(key: string): boolean {
  return /^[а-яё][а-яё\s-]+$/i.test(key)
    && key.includes(' ')
    && !/^speaker_/i.test(key)
    && !/^спикер\s*\d+/i.test(key);
}

const LEADER_ROLE: Omit<ResolvedSpeakerRole, 'personName' | 'orgHint'> = {
  label: 'Руководитель',
  variant: 'chair',
};

const TTK_EMPLOYEE: Omit<ResolvedSpeakerRole, 'personName' | 'orgHint'> = {
  label: 'Сотрудник ТТК',
  variant: 'ttk',
};

const ROLE_BY_PERSON: Record<string, Omit<ResolvedSpeakerRole, 'personName' | 'orgHint'>> = {
  'мухамед-рахимов галихан уразович': LEADER_ROLE,
  'галихан мухамед-рахимов': LEADER_ROLE,
  'галихан уразович': LEADER_ROLE,
  'даурен омарханов': TTK_EMPLOYEE,
  'дархан омаров': TTK_EMPLOYEE,
  'нуралы абдыхалыков': TTK_EMPLOYEE,
  'лаура арапова': { label: 'Сотрудник ТТК', variant: 'ttkAlt' },
  'ирина сурьма': TTK_EMPLOYEE,
  'инжу жансерик-козлова': TTK_EMPLOYEE,
  'speaker_04': TTK_EMPLOYEE,
  'спикер 04': TTK_EMPLOYEE,
  'алихан батырханулы': { label: 'Директор филиала', variant: 'director' },
};

const ORG_SUBTITLE: Record<string, string> = {
  'мухамед-рахимов галихан уразович': 'АО «Транстелеком»',
  'галихан мухамед-рахимов': 'АО «Транстелеком»',
  'галихан уразович': 'АО «Транстелеком»',
  'даурен омарханов': 'ТТК',
  'дархан омаров': 'Шымкенттранстелеком',
  'алихан батырханулы': 'АО «Жамбылтранстелеком»',
  'нуралы абдыхалыков': 'ТТК',
  'лаура арапова': 'ТТК',
  'ирина сурьма': 'ТТК',
  'инжу жансерик-козлова': 'ТТК',
  'speaker_04': 'ТТК',
  'спикер 04': 'ТТК',
};

function resolveFromName(
  personName: string,
  orgHint: string,
  key: string,
  combined: string,
): ResolvedSpeakerRole {
  if (isGalikhanLeader(key)) {
    return {
      personName,
      orgHint: orgHint || ORG_SUBTITLE[key] || 'ТТК',
      ...LEADER_ROLE,
    };
  }

  const known = ROLE_BY_PERSON[key];
  if (known) {
    return {
      personName,
      orgHint: orgHint || ORG_SUBTITLE[key] || '',
      ...known,
    };
  }

  if (combined.includes('председател') || combined.includes('руководител')) {
    return { personName, orgHint, ...LEADER_ROLE };
  }
  if (combined.includes('директор') || combined.includes('жамбылтранстелеком')) {
    return { personName, orgHint, label: 'Директор филиала', variant: 'director' };
  }
  if (orgHint.toUpperCase() === 'ТТК' || combined.includes('(ттк)')) {
    return { personName, orgHint: 'ТТК', ...TTK_EMPLOYEE };
  }
  if (combined.includes('секретар')) {
    return { personName, orgHint, label: 'Секретарь', variant: 'secretary' };
  }
  if (looksLikeMappedPerson(key)) {
    return { personName, orgHint: orgHint || 'ТТК', ...TTK_EMPLOYEE };
  }

  return {
    personName,
    orgHint,
    label: orgHint || 'Участник',
    variant: 'default',
  };
}

export function resolveSpeakerRole(raw: string, explicitRole?: string): ResolvedSpeakerRole {
  const { personName, orgHint } = parseSpeakerLine(raw);
  const key = normalizePersonKey(personName);
  const combined = `${personName} ${orgHint} ${explicitRole || ''}`.toLowerCase();

  const fromName = resolveFromName(personName, orgHint, key, combined);

  if (explicitRole && isBadgeRoleLabel(explicitRole)) {
    const badge = roleFromText(explicitRole);
    return {
      ...fromName,
      label: badge.label,
      variant: badge.variant,
      orgHint: fromName.orgHint || orgHint || ORG_SUBTITLE[key] || '',
    };
  }

  if (explicitRole && isJobTitleRole(explicitRole)) {
    return {
      ...fromName,
      orgHint: fromName.orgHint || explicitRole.trim(),
    };
  }

  return fromName;
}

function roleFromText(text: string): Pick<ResolvedSpeakerRole, 'label' | 'variant'> {
  const lower = text.toLowerCase();
  if (lower === 'chair' || lower.includes('руководител')) return LEADER_ROLE;
  if (lower.includes('председател')) return LEADER_ROLE;
  if (lower.includes('директор филиала')) return { label: 'Директор филиала', variant: 'director' };
  if (lower.includes('ттк') && lower.includes('сотрудник')) return TTK_EMPLOYEE;
  if (lower.includes('ттк')) return TTK_EMPLOYEE;
  if (lower.includes('секретар')) return { label: 'Секретарь', variant: 'secretary' };
  if (lower.includes('докладчик')) return { label: 'Докладчик', variant: 'reporter' };
  return { label: text.trim() || 'Участник', variant: variantFromLabel(text) };
}

function variantFromLabel(label: string): RoleBadgeVariant {
  const lower = label.toLowerCase();
  if (lower === 'chair' || lower.includes('руководител') || lower.includes('председател')) return 'chair';
  if (lower.includes('директор')) return 'director';
  if (lower.includes('ттк')) return 'ttk';
  if (lower.includes('секретар')) return 'secretary';
  if (lower.includes('докладчик')) return 'reporter';
  return 'default';
}

export function roleBadgeClassName(variant: RoleBadgeVariant): string {
  return variant;
}

export function parseParticipantMinutes(raw?: string): number {
  const text = (raw || '').trim().toLowerCase();
  if (!text || text === '—') return 0;
  const minMatch = text.match(/([\d.,]+)\s*мин/);
  const secMatch = text.match(/([\d.,]+)\s*сек/);
  const minutes = minMatch ? parseFloat(minMatch[1].replace(',', '.')) : 0;
  const seconds = secMatch ? parseFloat(secMatch[1].replace(',', '.')) : 0;
  if (!minutes && !seconds) {
    const num = parseFloat(text.replace(/[^\d.,]/g, '').replace(',', '.'));
    return Number.isFinite(num) ? num : 0;
  }
  return minutes + seconds / 60;
}

export function formatParticipantMinutes(raw?: string): string {
  const n = parseParticipantMinutes(raw);
  if (n > 0) return `${n.toFixed(1)} мин`;
  const cleaned = (raw || '').trim();
  if (/^[\d.,]+$/.test(cleaned)) return `${cleaned} мин`;
  if (cleaned && cleaned !== '—' && !/участник/i.test(cleaned)) return cleaned;
  return '—';
}

export function sortParticipantsByRole<T extends { name: string; role?: string; minutes?: string }>(
  items: T[],
): T[] {
  return [...items].sort((a, b) => {
    const ra = resolveSpeakerRole(a.name, a.role);
    const rb = resolveSpeakerRole(b.name, b.role);
    const orderDiff = ROLE_SORT_ORDER[ra.variant] - ROLE_SORT_ORDER[rb.variant];
    if (orderDiff !== 0) return orderDiff;
    return parseParticipantMinutes(b.minutes) - parseParticipantMinutes(a.minutes);
  });
}
